import { deleteUser, User } from '@back-office/http';
import { Instance } from 'mobx-state-tree';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMount } from 'react-use';
import { STATUS_CODE_SUCCESS } from '../../constants';
import useInject from '../../hooks/useInject';
import AuthModel from '../../stores/auth.store';
import PermissionsTypeModel from '../../stores/permissions.store';
import UsersModel from '../../stores/users.store';

type UseViewModelReturn = {
  auth: Instance<typeof AuthModel>;
  users: Instance<typeof UsersModel>;
  getUserList: () => void;
  permissionsList: Instance<typeof PermissionsTypeModel>;
  getPermissionsList: () => void;
  handleOnClickRemove: (user: User) => void;
};

const useViewModel = (): UseViewModelReturn => {
  const {
    auth,
    users,
    alert,
    getUserList,
    permissionsList,
    getPermissionsList,
  } = useInject((store) => store);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useMount(async () => {
    await getPermissionsList();
    getUserList();
  });

  const openSnackbar = async (isSuccess: boolean): Promise<void> => {
    if (isSuccess) {
      enqueueSnackbar(t('invite_new_user.success_delete_user'), {
        variant: 'success',
      });
    } else {
      enqueueSnackbar(t('invite_new_user.failed_delete_user'), {
        variant: 'error',
      });
    }
    await getUserList();
  };

  const handleOnClickRemove = (user: User): void => {
    alert.openAlert({
      title: 'dialog.confirmation',
      cancelBtn: 'dialog.cancel',
      okBtn: 'dialog.confirm',
      message: 'invite_new_user.confirm_delete_user',
      handleCancel() {
        alert.closeDialog();
      },
      async handleOK() {
        await deleteUser(user.email)
          .then(({ data: { status } }) => {
            openSnackbar(status.code === STATUS_CODE_SUCCESS);
            alert.closeDialog();
          })
          .catch(() => {
            openSnackbar(false);
            alert.closeDialog();
          })
          .finally(() => alert.closeDialog());
      },
    });
  };

  return {
    auth,
    users,
    getUserList,
    permissionsList,
    getPermissionsList,
    handleOnClickRemove,
  };
};

export default useViewModel;
